import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Lock, FileText, Languages, Eye, Pencil, Layers, Sparkles } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PageHero, Section, SectionHeading } from "@/components/blocks";
import BuyButton from "@/components/BuyButton";
import FreeDownloadButton from "@/components/FreeDownloadButton";
import { trackViewItem } from "@/lib/analytics";
import DeckBundles from "@/components/DeckBundles";
import api, { API as API_BASE } from "@/lib/api";

export default function Resources() {
  const [resources, setResources] = useState([]);
  const [filter, setFilter] = useState("All");

  useEffect(() => { api.get("/resources").then((r) => setResources(r.data)).catch(() => {}); }, []);

  const filtered = resources.filter((r) => filter === "All" || r.category === filter);

  return (
    <div data-testid="resources-page">
      <PageHero
        eyebrow="Digital Resource Store"
        title="Resources that make you placement ready."
        subtitle="Free toolkits and premium guides — created by educators, mapped to the standards, ready to download."
        image="https://images.unsplash.com/photo-1524178232363-1fb2b075b655?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600"
      />
      <Section>
        <SectionHeading eyebrow="Browse the store" title="Free & premium resources" />
        {filter === "Workshop Decks" && (
          <>
            <DeckBundles />
            <div className="mb-8 -mt-2">
              <Button asChild variant="outline" className="rounded-full" data-testid="resources-series-link">
                <Link to="/workshop-series"><Layers className="w-4 h-4" /> See the full series, slide by slide</Link>
              </Button>
            </div>
          </>
        )}
        {filter === "AI Workshops" && (
          <div className="mb-8 rounded-2xl border border-teal-200 bg-teal-50/60 p-6" data-testid="resources-ai-banner">
            <h3 className="font-display text-lg font-bold text-slate-900">Sold as a level, not as single decks</h3>
            <p className="text-sm text-slate-600 mt-2 max-w-2xl">
              Each AI level is $49 and includes all five workshop decks. Preview any slide below, then take the level.
            </p>
            <Button asChild className="rounded-full bg-teal-600 hover:bg-teal-700 mt-4" data-testid="resources-ai-link">
              <Link to="/ai-workshops"><Sparkles className="w-4 h-4" /> See the AI workshop series</Link>
            </Button>
          </div>
        )}
        <Tabs value={filter} onValueChange={setFilter} className="mb-8">
          <div className="-mx-4 px-4 overflow-x-auto sm:mx-0 sm:px-0 sm:overflow-visible">
            <TabsList data-testid="resources-filter" className="w-max sm:w-auto">
              <TabsTrigger value="All" data-testid="resources-tab-all">All</TabsTrigger>
              <TabsTrigger value="Free" data-testid="resources-tab-free">Free Toolkits</TabsTrigger>
              <TabsTrigger value="Paid" data-testid="resources-tab-paid">Paid Guides</TabsTrigger>
              <TabsTrigger value="Workshop Decks" data-testid="resources-tab-decks">Workshop Decks</TabsTrigger>
              <TabsTrigger value="AI Workshops" data-testid="resources-tab-ai">AI Workshops</TabsTrigger>
              <TabsTrigger value="Language & Culture" data-testid="resources-tab-language">Language &amp; Culture <span className="ml-1.5 text-teal-600 font-semibold">中文</span></TabsTrigger>
            </TabsList>
          </div>
        </Tabs>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filtered.map((r) => (
            <Card key={r.id} className="flex flex-col border-slate-200 hover:shadow-lg transition-shadow overflow-hidden" data-testid={`resource-card-${r.id}`}>
              {r.has_thumbnail && (
                <a href={`${API_BASE}/resources/${r.id}/preview`} target="_blank" rel="noreferrer" className="block bg-slate-100 border-b border-slate-200 group" data-testid={`resource-thumb-${r.id}`}>
                  <img src={`${API_BASE}/resources/${r.id}/thumbnail`} alt={`${r.title} — first slide`} loading="lazy"
                       className="w-full aspect-[16/9] object-cover object-top group-hover:opacity-90 transition-opacity" />
                </a>
              )}
              <div className="p-6 flex flex-col flex-1">
              <div className="flex items-start justify-between mb-4">
                <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${r.bilingual ? "bg-teal-100" : r.price === 0 ? "bg-emerald-100" : "bg-teal-100"}`}>
                  {r.bilingual ? <Languages className="w-5 h-5 text-teal-700" /> : r.price === 0 ? <FileText className="w-5 h-5 text-emerald-600" /> : <Lock className="w-5 h-5 text-teal-600" />}
                </div>
                <div className="flex gap-1.5">
                  {r.bilingual && <Badge className="bg-slate-900 text-white hover:bg-slate-900">English · 中文</Badge>}
                  <Badge className={r.price === 0 ? "bg-emerald-100 text-emerald-800 hover:bg-emerald-100" : "bg-slate-900 text-white hover:bg-slate-900"}>
                    {r.price === 0 ? "Free" : `$${r.price}`}
                  </Badge>
                </div>
              </div>
              <h3 className="font-display text-lg font-bold text-slate-900">{r.title}</h3>
              <p className="text-sm text-slate-600 mt-2 flex-1">{r.description}</p>
              <div className="text-xs text-slate-400 mt-3 mb-4 flex items-center gap-1"><FileText className="w-3 h-3" /> {r.format}</div>
              {r.category === "Workshop Decks" && (
                <p className="text-xs text-teal-700 bg-teal-50 border border-teal-100 rounded-lg px-3 py-2 mb-4 flex items-start gap-1.5" data-testid={`resource-editable-note-${r.id}`}>
                  <Pencil className="w-3 h-3 mt-0.5 shrink-0" />
                  <span>Fully editable PowerPoint — mentors and schools can adapt the slides for their own sessions.</span>
                </p>
              )}
              <div className="space-y-2">
                {r.level_id ? (
                  <Button asChild className="w-full bg-teal-600 hover:bg-teal-700 rounded-full" data-testid={`resource-level-${r.id}`}>
                    <Link to="/ai-workshops"><Sparkles className="w-4 h-4" /> Included in {r.level_name?.split(" — ")[0]} · ${r.price}</Link>
                  </Button>
                ) : r.price === 0 ? <FreeDownloadButton resource={r} className="w-full bg-teal-600 hover:bg-teal-700 rounded-full" /> : (
                  <BuyButton itemId={r.id} itemName={r.title} price={r.price} className="w-full bg-slate-900 hover:bg-teal-700 rounded-full" testId={`resource-buy-${r.id}`}>
                    Add to basket · ${r.price}
                  </BuyButton>
                )}
                {r.has_preview && (
                  <Button asChild variant="outline" className="w-full rounded-full" data-testid={`resource-preview-${r.id}`}>
                    <a href={`${API_BASE}/resources/${r.id}/preview`} target="_blank" rel="noreferrer"
                       onClick={() => trackViewItem({ item_id: r.id, name: r.title, price: r.price, kind: "resource" })}>
                      <Eye className="w-4 h-4" /> Preview a sample page
                    </a>
                  </Button>
                )}
              </div>
              </div>
            </Card>
          ))}
        </div>
      </Section>
    </div>
  );
}
